// KI-API: beliebig viele (1:n) einsatzfertige Konfigurationen. Die Auswahl ist
// stufenweise voneinander abhaengig: Anbieter -> Bereich -> Modell. Mit der
// Modellwahl werden Parameter (Standardwerte) und System-Prompt aus den
// KI-Stammdaten vorbelegt. Alle Auswahlwerte stammen aus den Stammdaten.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, Container, Modal, Form, Badge, Alert, Spinner, Row, Col } from "react-bootstrap";
import { FaEdit, FaTrash, FaFlask } from "react-icons/fa";
import { Link } from "react-router-dom";
import AiApiService from "../services/ai-api.service";
import EventBus from "../common/EventBus";
import ErrorModal from "./ErrorModal";

const isActive = (row) => !(row.is_active === false || row.is_active === 0);

const emptyForm = {
  config_name: "",
  ai_provider_id: "",
  ai_category_id: "",
  ai_model_id: "",
  base_url: "",
  api_key: "",
  params: {},
  system_prompt: "",
  description: "",
  is_default: false,
  is_active: true,
};

const BoardAiConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [md, setMd] = useState({ providers: [], categories: [], models: [], params: [] });
  const [editing, setEditing] = useState(null); // null | "new" | config
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState({ show: false, message: "", code: "" });
  const [testing, setTesting] = useState(null); // config
  const [testPrompt, setTestPrompt] = useState("");
  const [testVars, setTestVars] = useState('{\n  "company_name": "Bäckerei Muster",\n  "category": "Bäckerei",\n  "services": "Brot, Brötchen, Kuchen",\n  "description": "",\n  "city": "Köln"\n}');
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);

  const showApiError = useCallback((err, fallback) => {
    const res = err && err.response;
    setError({ show: true, message: (res && res.data && res.data.message) || fallback, code: res ? `HTTP ${res.status}` : "" });
    if (res && res.status === 401) EventBus.dispatch("logout");
  }, []);

  const reload = useCallback(() => {
    Promise.all([
      AiApiService.listConfigs(),
      AiApiService.listMasterData("providers"),
      AiApiService.listMasterData("categories"),
      AiApiService.listMasterData("models"),
      AiApiService.listMasterData("model-params"),
    ])
      .then(([c, p, cat, m, par]) => {
        setConfigs(c.data || []);
        setMd({ providers: p.data || [], categories: cat.data || [], models: m.data || [], params: par.data || [] });
      })
      .catch((err) => showApiError(err, "Die KI-API-Konfigurationen konnten nicht geladen werden."));
  }, [showApiError]);

  useEffect(() => {
    reload();
  }, [reload]);

  const byId = (list, key, id) => list.find((r) => `${r[key]}` === `${id}`);
  const providerName = (id) => (byId(md.providers, "ai_provider_id", id) || {}).provider_name || `#${id}`;
  const categoryName = (id) => (byId(md.categories, "ai_category_id", id) || {}).category_name || `#${id}`;
  const modelName = (id) => (byId(md.models, "ai_model_id", id) || {}).model_name || `#${id}`;

  // --- Abhaengige Auswahllisten -------------------------------------------
  const activeModels = useMemo(() => md.models.filter(isActive), [md.models]);
  const providerOptions = md.providers.filter(isActive);
  const categoryOptions = md.categories.filter(
    (c) => isActive(c) && activeModels.some((m) => `${m.ai_provider_id}` === `${form.ai_provider_id}` && m.ai_category_id === c.ai_category_id)
  );
  const modelOptions = activeModels.filter(
    (m) => `${m.ai_provider_id}` === `${form.ai_provider_id}` && `${m.ai_category_id}` === `${form.ai_category_id}`
  );
  const modelParams = md.params
    .filter((p) => `${p.ai_model_id}` === `${form.ai_model_id}`)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const selectProvider = (id) => setForm((f) => ({ ...f, ai_provider_id: id, ai_category_id: "", ai_model_id: "", params: {} }));
  const selectCategory = (id) => setForm((f) => ({ ...f, ai_category_id: id, ai_model_id: "", params: {} }));
  const selectModel = (id) => {
    const model = byId(md.models, "ai_model_id", id);
    const defaults = {};
    md.params
      .filter((p) => `${p.ai_model_id}` === `${id}`)
      .forEach((p) => {
        defaults[p.param_key] = p.default_value ?? "";
      });
    setForm((f) => ({ ...f, ai_model_id: id, params: defaults, system_prompt: (model && model.default_system_prompt) || "" }));
  };

  const openEdit = (row) => {
    setFieldErrors({});
    if (!row) {
      setForm(emptyForm);
      setEditing("new");
      return;
    }
    setForm({
      ...emptyForm,
      config_name: row.config_name || "",
      ai_provider_id: `${row.ai_provider_id}`,
      ai_category_id: `${row.ai_category_id}`,
      ai_model_id: `${row.ai_model_id}`,
      base_url: row.base_url || "",
      params: row.params || {},
      system_prompt: row.system_prompt || "",
      description: row.description || "",
      is_default: !!row.is_default,
      is_active: isActive(row),
    });
    setEditing(row);
  };

  const resetToDefaults = () => selectModel(form.ai_model_id);

  const save = () => {
    const payload = { ...form };
    if (!payload.api_key) delete payload.api_key; // leer = vorhandenen Schluessel behalten
    const req = editing === "new" ? AiApiService.createConfig(payload) : AiApiService.updateConfig(editing.ai_config_id, payload);
    req
      .then(() => {
        setEditing(null);
        reload();
      })
      .catch((err) => {
        const res = err && err.response;
        if (res && res.status === 422 && res.data && res.data.errors) setFieldErrors(res.data.errors);
        else showApiError(err, "Die Konfiguration konnte nicht gespeichert werden.");
      });
  };

  const remove = (row) => {
    if (!window.confirm(`Konfiguration „${row.config_name}“ wirklich löschen?`)) return;
    AiApiService.deleteConfig(row.ai_config_id)
      .then(reload)
      .catch((err) => showApiError(err, "Die Konfiguration konnte nicht gelöscht werden."));
  };

  const runTest = () => {
    let vars = {};
    try {
      vars = testVars.trim() ? JSON.parse(testVars) : {};
    } catch (_) {
      setTestResult({ ok: false, message: "Die Testvariablen sind kein gültiges JSON." });
      return;
    }
    setTestBusy(true);
    setTestResult(null);
    AiApiService.testConfig(testing.ai_config_id, { prompt: testPrompt, vars })
      .then((res) => setTestResult(res.data))
      .catch((err) => setTestResult({ ok: false, message: (err.response && err.response.data && err.response.data.message) || err.message }))
      .finally(() => setTestBusy(false));
  };

  const err = (k) => fieldErrors[k] && <div className="invalid-feedback d-block">{fieldErrors[k]}</div>;

  const renderParam = (p) => {
    const value = form.params[p.param_key] ?? "";
    const set = (v) => setForm((f) => ({ ...f, params: { ...f.params, [p.param_key]: v } }));
    let control;
    if (p.param_type === "select") {
      const options = String(p.options || "").split(/\r?\n/).map((o) => o.trim()).filter(Boolean);
      control = (
        <Form.Select value={value} onChange={(e) => set(e.target.value)}>
          <option value="">– Anbieter-Standard –</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Form.Select>
      );
    } else if (p.param_type === "boolean") {
      control = <Form.Check type="switch" checked={value === true || value === "true" || value === "1"} onChange={(e) => set(e.target.checked ? "true" : "false")} />;
    } else {
      control = <Form.Control type={p.param_type === "number" ? "number" : "text"} step="any" value={value} onChange={(e) => set(e.target.value)} />;
    }
    return (
      <Col md={6} key={p.ai_model_param_id}>
        <Form.Group className="mb-3">
          <Form.Label>{p.param_label} <code className="small">{p.param_key}</code></Form.Label>
          {control}
          {p.description && <Form.Text className="text-muted">{p.description}</Form.Text>}
          {err(`params.${p.param_key}`)}
        </Form.Group>
      </Col>
    );
  };

  return (
    <Container className="mt-4">
      <h3>KI-API</h3>
      <p className="text-muted">
        Beliebig viele KI-Konfigurationen. Pro Bereich kann eine Konfiguration als Standard markiert werden – das
        automatische Kartenbild unter „Gerade erzeugt“ nutzt die Standard-Konfiguration des Bereichs mit dem Schlüssel
        <code> IMAGE</code>. Auswahlwerte pflegen Sie unter <Link to="/ai-stammdaten">KI-Stammdaten</Link>.
      </p>
      <Button className="mb-3" onClick={() => openEdit(null)}>+ KI-API anlegen</Button>
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>Name</th><th>Anbieter</th><th>Bereich</th><th>Modell</th><th>Schlüssel</th><th>Status</th><th style={{ width: 140 }}>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {configs.map((row) => (
            <tr key={row.ai_config_id}>
              <td>{row.config_name} {row.is_default && <Badge bg="primary">Standard</Badge>}</td>
              <td>{providerName(row.ai_provider_id)}</td>
              <td>{categoryName(row.ai_category_id)}</td>
              <td>{modelName(row.ai_model_id)}</td>
              <td>{row.has_api_key ? <Badge bg="success">hinterlegt</Badge> : <Badge bg="warning" text="dark">fehlt</Badge>}</td>
              <td>{isActive(row) ? <Badge bg="success">aktiv</Badge> : <Badge bg="secondary">inaktiv</Badge>}</td>
              <td>
                <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(row)} title="Bearbeiten"><FaEdit /></Button>
                <Button size="sm" variant="outline-success" className="me-1" title="Testlauf" onClick={() => { setTesting(row); setTestResult(null); setTestPrompt(""); }}><FaFlask /></Button>
                <Button size="sm" variant="outline-danger" onClick={() => remove(row)} title="Löschen"><FaTrash /></Button>
              </td>
            </tr>
          ))}
          {configs.length === 0 && (
            <tr><td colSpan={7} className="text-center text-muted">Noch keine KI-API angelegt.</td></tr>
          )}
        </tbody>
      </Table>

      <Modal show={!!editing} onHide={() => setEditing(null)} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editing === "new" ? "KI-API anlegen" : "KI-API bearbeiten"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control value={form.config_name} isInvalid={!!fieldErrors.config_name} onChange={(e) => setForm({ ...form, config_name: e.target.value })} />
                {err("config_name")}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>1. Anbieter *</Form.Label>
                <Form.Select value={form.ai_provider_id} onChange={(e) => selectProvider(e.target.value)}>
                  <option value="">– bitte wählen –</option>
                  {providerOptions.map((p) => <option key={p.ai_provider_id} value={p.ai_provider_id}>{p.provider_name}</option>)}
                </Form.Select>
                {err("ai_provider_id")}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>2. Bereich *</Form.Label>
                <Form.Select value={form.ai_category_id} disabled={!form.ai_provider_id} onChange={(e) => selectCategory(e.target.value)}>
                  <option value="">{form.ai_provider_id && categoryOptions.length === 0 ? "– keine Modelle für diesen Anbieter –" : "– bitte wählen –"}</option>
                  {categoryOptions.map((c) => <option key={c.ai_category_id} value={c.ai_category_id}>{c.category_name}</option>)}
                </Form.Select>
                {err("ai_category_id")}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>3. Modell *</Form.Label>
                <Form.Select value={form.ai_model_id} disabled={!form.ai_category_id} onChange={(e) => selectModel(e.target.value)}>
                  <option value="">– bitte wählen –</option>
                  {modelOptions.map((m) => <option key={m.ai_model_id} value={m.ai_model_id}>{m.model_name} ({m.model_code})</option>)}
                </Form.Select>
                {err("ai_model_id")}
              </Form.Group>
            </Col>
          </Row>

          {form.ai_model_id && (
            <>
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-2">4. Modellspezifische Konfiguration</h6>
                <Button size="sm" variant="link" onClick={resetToDefaults}>Standardwerte & System-Prompt wiederherstellen</Button>
              </div>
              <Row>
                {modelParams.map(renderParam)}
                {modelParams.length === 0 && <Col><p className="text-muted small">Für dieses Modell sind keine Parameter definiert.</p></Col>}
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>System-Prompt</Form.Label>
                <Form.Control as="textarea" rows={8} value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} />
                <Form.Text className="text-muted">
                  Platzhalter für das Kartenbild: {"{{company_name}}, {{category}}, {{services}}, {{description}}, {{city}}, {{address}}"}
                </Form.Text>
              </Form.Group>
            </>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>API-Schlüssel {editing && editing !== "new" && editing.has_api_key ? "(hinterlegt – leer lassen = unverändert)" : ""}</Form.Label>
                <Form.Control type="password" autoComplete="new-password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
                <Form.Text className="text-muted">Wird verschlüsselt gespeichert und nie wieder angezeigt.</Form.Text>
                {err("api_key")}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Abweichende Basis-URL (optional)</Form.Label>
                <Form.Control value={form.base_url} placeholder="leer = Basis-URL des Anbieters" onChange={(e) => setForm({ ...form, base_url: e.target.value })} />
                {err("base_url")}
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Beschreibung</Form.Label>
            <Form.Control value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Form.Group>
          <Form.Check type="switch" className="mb-2" label="Standard für diesen Bereich" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
          <Form.Check type="switch" label="Aktiv" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Button>
          <Button variant="primary" onClick={save}>Speichern</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!testing} onHide={() => setTesting(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Testlauf: {testing && testing.config_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small">
            Der Testlauf ruft den KI-Anbieter wirklich auf und kann Kosten verursachen. Ergebnisse werden nicht gespeichert.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>Testvariablen für Platzhalter (JSON)</Form.Label>
            <Form.Control as="textarea" rows={6} className="font-monospace small" value={testVars} onChange={(e) => setTestVars(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Zusätzlicher Prompt (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={testPrompt} onChange={(e) => setTestPrompt(e.target.value)} />
          </Form.Group>
          {testResult && !testResult.ok && <Alert variant="danger">{testResult.message}</Alert>}
          {testResult && testResult.ok && (
            <div>
              <p className="small text-muted">Dauer: {testResult.duration_ms} ms</p>
              {testResult.type === "image" ? (
                <img src={testResult.image} alt="Testergebnis" className="img-fluid rounded border" />
              ) : (
                <pre className="bg-light p-2 rounded" style={{ whiteSpace: "pre-wrap" }}>{testResult.text}</pre>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTesting(null)}>Schließen</Button>
          <Button variant="success" onClick={runTest} disabled={testBusy}>
            {testBusy ? <><Spinner size="sm" animation="border" /> Läuft …</> : "Test starten"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ErrorModal show={error.show} handleClose={() => setError({ show: false, message: "", code: "" })} errorMessage={error.message} errorCode={error.code} />
    </Container>
  );
};

export default BoardAiConfigs;
